import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FaAward,
  FaCheckCircle,
  FaClipboardCheck,
  FaEdit,
  FaExclamationTriangle,
  FaPlus,
  FaSave,
  FaUsers,
} from "react-icons/fa";
import Header from "../../../components/ManagementHeader";
import Footer from "../../../components/Footer";
import PageLoader from "../../../components/PageLoader";
import { supabase } from "../../../supabaseClient";
import { useFeatureFlags } from "../../../contexts/FeatureFlagsContext";
import {
  competencyStatusClass,
  formatCompetencyDate,
  getCompetencyStatus,
  isCompetencyRequiredForRole,
} from "../../../utils/competencies";
import { formatProfileName } from "../../../utils/profileName";

const EMPTY_DEFINITION = {
  id: null,
  title: "",
  category: "General",
  description: "",
  default_validity_months: 12,
  required_job_roles: [],
  renewal_elearning_module_id: "",
  renewal_lead_days: 30,
  active: true,
};

const EMPTY_RECORD = {
  id: null,
  user_id: "",
  competency_id: "",
  achieved_on: "",
  expires_on: "",
  certificate_reference: "",
  evidence_url: "",
  evidence_notes: "",
  notes: "",
  verified: true,
};

const EMPTY_BULK_ASSIGNMENT = {
  staff_ids: [],
  competency_ids: [],
  achieved_on: "",
  evidence_notes: "",
  notes: "",
  verified: false,
};

function addMonthsISO(dateValue, months) {
  if (!dateValue || !Number(months)) return "";
  const date = new Date(`${dateValue}T12:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  const originalDay = date.getDate();
  date.setDate(1);
  date.setMonth(date.getMonth() + Number(months));
  const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  date.setDate(Math.min(originalDay, lastDay));
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

export default function ManagementCompetencies({ session, profile }) {
  const { isEnabled } = useFeatureFlags();
  const [tab, setTab] = useState("catalogue");
  const [catalogue, setCatalogue] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [records, setRecords] = useState([]);
  const [learningModules, setLearningModules] = useState([]);
  const [definitionDraft, setDefinitionDraft] = useState({ ...EMPTY_DEFINITION });
  const [recordDraft, setRecordDraft] = useState({ ...EMPTY_RECORD });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDraft, setBulkDraft] = useState({ ...EMPTY_BULK_ASSIGNMENT });
  const [bulkStaffSearch, setBulkStaffSearch] = useState("");
  const [bulkCompetencySearch, setBulkCompetencySearch] = useState("");
  const [search, setSearch] = useState("");
  const [complianceFilter, setComplianceFilter] = useState("attention");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [verifyingRecordId, setVerifyingRecordId] = useState(null);
  const [feedback, setFeedback] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFeedback(null);

    const [catalogueResult, profileResult, recordResult, learningResult] = await Promise.all([
      supabase
        .from("staff_competency_catalog")
        .select("*")
        .order("active", { ascending: false })
        .order("category", { ascending: true })
        .order("title", { ascending: true }),
      supabase
        .from("profiles")
        .select("id, full_name, name_prefix, email, position, job_role, suspended, discord_id")
        .in("position", ["Staff", "Management"])
        .order("full_name", { ascending: true }),
      supabase
        .from("staff_competency_records")
        .select("*")
        .order("expires_on", { ascending: true, nullsFirst: false }),
      supabase
        .from("e_learning_modules")
        .select("id, title, category, mandatory, active")
        .eq("active", true)
        .order("title", { ascending: true }),
    ]);

    const firstError = catalogueResult.error || profileResult.error || recordResult.error;
    if (firstError) {
      console.error("Unable to load competency management:", firstError);
      setCatalogue([]);
      setProfiles([]);
      setRecords([]);
      setLearningModules([]);
      setFeedback({
        type: "error",
        text: "The competency tracker could not be loaded. Run ADD_STAFF_COMPETENCIES.sql first.",
      });
    } else {
      setCatalogue(catalogueResult.data || []);
      setProfiles((profileResult.data || []).filter((item) => item.suspended !== true));
      setRecords(recordResult.data || []);
      setLearningModules(learningResult.error ? [] : learningResult.data || []);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    void load();
  }, []);

  const profileMap = useMemo(
    () => Object.fromEntries(profiles.map((item) => [item.id, item])),
    [profiles],
  );
  const definitionMap = useMemo(
    () => Object.fromEntries(catalogue.map((item) => [item.id, item])),
    [catalogue],
  );
  const recordMap = useMemo(
    () => Object.fromEntries(records.map((record) => [`${record.user_id}:${record.competency_id}`, record])),
    [records],
  );

  const jobRoles = useMemo(
    () => Array.from(new Set(
      profiles.map((item) => String(item.job_role || "").trim()).filter(Boolean),
    )).sort((a, b) => a.localeCompare(b)),
    [profiles],
  );

  const complianceRows = useMemo(() => {
    const rows = [];
    profiles.forEach((staff) => {
      catalogue.filter((definition) => definition.active && isCompetencyRequiredForRole(definition, staff.job_role)).forEach((definition) => {
        const record = recordMap[`${staff.id}:${definition.id}`] || null;
        rows.push({
          staff,
          definition,
          record,
          status: getCompetencyStatus(record, { required: true }),
        });
      });
    });
    return rows;
  }, [catalogue, profiles, recordMap]);

  const filteredComplianceRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return complianceRows.filter((row) => {
      const matchesQuery =
        !query ||
        formatProfileName(row.staff, "").toLowerCase().includes(query) ||
        String(row.staff.email || "").toLowerCase().includes(query) ||
        String(row.staff.job_role || "").toLowerCase().includes(query) ||
        String(row.definition.title || "").toLowerCase().includes(query);
      const matchesStatus =
        complianceFilter === "all" ||
        (complianceFilter === "attention" && row.status.attention) ||
        row.status.key === complianceFilter;
      return matchesQuery && matchesStatus;
    });
  }, [complianceFilter, complianceRows, search]);

  const filteredRecords = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return records;
    return records.filter((record) => {
      const staff = profileMap[record.user_id];
      const definition = definitionMap[record.competency_id];
      return [formatProfileName(staff, ""), staff?.email, staff?.job_role, definition?.title, definition?.category, record.certificate_reference]
        .some((value) => String(value || "").toLowerCase().includes(query));
    });
  }, [definitionMap, profileMap, records, search]);

  const bulkStaffOptions = useMemo(() => {
    const query = bulkStaffSearch.trim().toLowerCase();
    return profiles.filter((staff) => !query || [formatProfileName(staff, ""), staff.email, staff.job_role, staff.position]
      .some((value) => String(value || "").toLowerCase().includes(query)));
  }, [bulkStaffSearch, profiles]);

  const bulkCompetencyOptions = useMemo(() => {
    const query = bulkCompetencySearch.trim().toLowerCase();
    return catalogue.filter((item) => item.active && (!query || [item.title, item.category, item.description]
      .some((value) => String(value || "").toLowerCase().includes(query))));
  }, [bulkCompetencySearch, catalogue]);

  const summary = useMemo(() => {
    const requiredRows = complianceRows;
    return {
      required: requiredRows.length,
      valid: requiredRows.filter((row) => row.status.key === "valid").length,
      expiring: requiredRows.filter((row) => row.status.key === "expiring").length,
      expired: requiredRows.filter((row) => row.status.key === "expired").length,
      missing: requiredRows.filter((row) => row.status.key === "missing").length,
      pending: requiredRows.filter((row) => row.status.key === "pending").length,
    };
  }, [complianceRows]);

  function resetDefinition() {
    setDefinitionDraft({ ...EMPTY_DEFINITION });
  }

  function editDefinition(item) {
    setDefinitionDraft({
      id: item.id,
      title: item.title || "",
      category: item.category || "General",
      description: item.description || "",
      default_validity_months: item.default_validity_months ?? "",
      required_job_roles: Array.isArray(item.required_job_roles) ? item.required_job_roles : [],
      renewal_elearning_module_id: item.renewal_elearning_module_id || "",
      renewal_lead_days: item.renewal_lead_days || 30,
      active: item.active !== false,
    });
    setTab("catalogue");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleRequiredRole(role) {
    setDefinitionDraft((current) => ({
      ...current,
      required_job_roles: current.required_job_roles.includes(role)
        ? current.required_job_roles.filter((item) => item !== role)
        : [...current.required_job_roles, role],
    }));
  }

  async function saveDefinition(event) {
    event.preventDefault();
    if (!definitionDraft.title.trim()) {
      setFeedback({ type: "error", text: "Enter a competency / certification title." });
      return;
    }

    setWorking(true);
    setFeedback(null);
    const payload = {
      title: definitionDraft.title.trim(),
      category: definitionDraft.category.trim() || "General",
      description: definitionDraft.description.trim() || null,
      default_validity_months: definitionDraft.default_validity_months === ""
        ? null
        : Number(definitionDraft.default_validity_months),
      required_job_roles: definitionDraft.required_job_roles,
      renewal_elearning_module_id: definitionDraft.renewal_elearning_module_id || null,
      renewal_lead_days: Number(definitionDraft.renewal_lead_days) || 30,
      active: definitionDraft.active !== false,
    };

    let error = null;
    if (definitionDraft.id) {
      ({ error } = await supabase
        .from("staff_competency_catalog")
        .update(payload)
        .eq("id", definitionDraft.id));
    } else {
      ({ error } = await supabase
        .from("staff_competency_catalog")
        .insert({ ...payload, created_by: session?.user?.id || null }));
    }

    setWorking(false);
    if (error) {
      console.error("Unable to save competency definition:", error);
      setFeedback({ type: "error", text: error.message || "The competency could not be saved." });
      return;
    }

    setFeedback({ type: "success", text: "Competency catalogue updated." });
    resetDefinition();
    await load();
  }

  async function toggleDefinitionActive(item) {
    const { error } = await supabase
      .from("staff_competency_catalog")
      .update({ active: !item.active })
      .eq("id", item.id);
    if (error) {
      setFeedback({ type: "error", text: error.message || "The competency could not be updated." });
      return;
    }
    await load();
  }

  function resetRecord() {
    setRecordDraft({ ...EMPTY_RECORD });
  }

  function editRecord(record) {
    setRecordDraft({
      id: record.id,
      user_id: record.user_id || "",
      competency_id: record.competency_id || "",
      achieved_on: record.achieved_on || "",
      expires_on: record.expires_on || "",
      certificate_reference: record.certificate_reference || "",
      evidence_url: record.evidence_url || "",
      evidence_notes: record.evidence_notes || "",
      notes: record.notes || "",
      verified: record.verified !== false,
    });
    setTab("records");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function updateRecordField(field, value) {
    setRecordDraft((current) => {
      const next = { ...current, [field]: value };
      if (field === "competency_id" || field === "achieved_on") {
        const competencyId = field === "competency_id" ? value : current.competency_id;
        const achievedOn = field === "achieved_on" ? value : current.achieved_on;
        const definition = definitionMap[competencyId];
        if (!current.id && achievedOn && definition?.default_validity_months) {
          next.expires_on = addMonthsISO(achievedOn, definition.default_validity_months);
        }
      }
      return next;
    });
  }

  function toggleBulkSelection(field, id) {
    setBulkDraft((current) => ({
      ...current,
      [field]: current[field].includes(id)
        ? current[field].filter((item) => item !== id)
        : [...current[field], id],
    }));
  }

  function setBulkVisible(field, ids, checked) {
    setBulkDraft((current) => {
      const currentValues = new Set(current[field]);
      ids.forEach((id) => checked ? currentValues.add(id) : currentValues.delete(id));
      return { ...current, [field]: Array.from(currentValues) };
    });
  }

  function resetBulkAssignment() {
    setBulkDraft({ ...EMPTY_BULK_ASSIGNMENT });
    setBulkStaffSearch("");
    setBulkCompetencySearch("");
  }

  async function saveBulkAssignment(event) {
    event.preventDefault();
    if (!bulkDraft.staff_ids.length || !bulkDraft.competency_ids.length) {
      setFeedback({ type: "error", text: "Select at least one staff member and one competency." });
      return;
    }

    const combinations = [];
    let existingCount = 0;

    bulkDraft.staff_ids.forEach((userId) => {
      bulkDraft.competency_ids.forEach((competencyId) => {
        if (recordMap[`${userId}:${competencyId}`]) {
          existingCount += 1;
          return;
        }
        const definition = definitionMap[competencyId];
        combinations.push({
          user_id: userId,
          competency_id: competencyId,
          achieved_on: bulkDraft.achieved_on || null,
          expires_on: bulkDraft.achieved_on && definition?.default_validity_months
            ? addMonthsISO(bulkDraft.achieved_on, definition.default_validity_months)
            : null,
          certificate_reference: null,
          evidence_url: null,
          evidence_notes: bulkDraft.evidence_notes.trim() || null,
          notes: bulkDraft.notes.trim() || null,
          verified: bulkDraft.verified,
          verified_by: bulkDraft.verified ? session?.user?.id || null : null,
          verified_at: bulkDraft.verified ? new Date().toISOString() : null,
          verification_method: bulkDraft.verified ? "management" : null,
          verification_notes: bulkDraft.verified ? (bulkDraft.evidence_notes.trim() || null) : null,
          created_by: session?.user?.id || null,
        });
      });
    });

    if (!combinations.length) {
      setFeedback({ type: "success", text: `Nothing new to add. ${existingCount} existing assignment${existingCount === 1 ? " was" : "s were"} left unchanged.` });
      return;
    }

    setWorking(true);
    setFeedback(null);
    const { error } = await supabase
      .from("staff_competency_records")
      .upsert(combinations, { onConflict: "competency_id,user_id", ignoreDuplicates: true });
    setWorking(false);

    if (error) {
      console.error("Unable to bulk assign competencies:", error);
      setFeedback({ type: "error", text: error.message || "The bulk competency assignment could not be completed." });
      return;
    }

    const addedCount = combinations.length;
    setFeedback({
      type: "success",
      text: `${addedCount} competency assignment${addedCount === 1 ? "" : "s"} added${existingCount ? `; ${existingCount} existing assignment${existingCount === 1 ? " was" : "s were"} skipped.` : "."}`,
    });
    resetBulkAssignment();
    setBulkOpen(false);
    await load();
  }

  async function saveRecord(event) {
    event.preventDefault();
    if (!recordDraft.user_id || !recordDraft.competency_id) {
      setFeedback({ type: "error", text: "Select both a staff member and a competency." });
      return;
    }

    setWorking(true);
    setFeedback(null);

    const payload = {
      user_id: recordDraft.user_id,
      competency_id: recordDraft.competency_id,
      achieved_on: recordDraft.achieved_on || null,
      expires_on: recordDraft.expires_on || null,
      certificate_reference: recordDraft.certificate_reference.trim() || null,
      evidence_url: recordDraft.evidence_url.trim() || null,
      evidence_notes: recordDraft.evidence_notes.trim() || null,
      notes: recordDraft.notes.trim() || null,
      verified: recordDraft.verified,
      verified_by: recordDraft.verified ? session?.user?.id || null : null,
      verified_at: recordDraft.verified ? new Date().toISOString() : null,
      verification_method: recordDraft.verified ? "management" : null,
      verification_notes: recordDraft.verified ? (recordDraft.evidence_notes.trim() || null) : null,
    };

    let error = null;
    if (recordDraft.id) {
      ({ error } = await supabase
        .from("staff_competency_records")
        .update(payload)
        .eq("id", recordDraft.id));
    } else {
      ({ error } = await supabase
        .from("staff_competency_records")
        .upsert(
          { ...payload, created_by: session?.user?.id || null },
          { onConflict: "competency_id,user_id" },
        ));
    }

    setWorking(false);
    if (error) {
      console.error("Unable to save competency record:", error);
      setFeedback({ type: "error", text: error.message || "The staff competency record could not be saved." });
      return;
    }

    setFeedback({ type: "success", text: "Staff competency record saved." });
    resetRecord();
    await load();
  }

  async function managementVerify(record) {
    if (!record?.id || verifyingRecordId) return;
    const notes = window.prompt(
      "Optional Management verification note:",
      record.verification_notes || record.evidence_notes || "",
    );
    if (notes === null) return;

    setVerifyingRecordId(record.id);
    setFeedback(null);
    const { error } = await supabase.rpc("management_verify_staff_competency", {
      p_record_id: record.id,
      p_notes: String(notes || "").trim() || null,
    });
    setVerifyingRecordId(null);

    if (error) {
      console.error("Unable to verify competency:", error);
      setFeedback({ type: "error", text: error.message || "The competency could not be verified." });
      return;
    }

    setFeedback({ type: "success", text: "Competency verified by Management." });
    await load();
  }

  function verificationLabel(record) {
    if (!record?.verified) return "Awaiting verification";
    return record.verification_method === "self" ? "Self verified" : "Management verified";
  }

  async function deleteRecord(record) {
    const staff = profileMap[record.user_id];
    const definition = definitionMap[record.competency_id];
    if (!window.confirm(`Remove ${definition?.title || "this competency"} from ${formatProfileName(staff, "this staff member")}?`)) return;
    const { error } = await supabase.from("staff_competency_records").delete().eq("id", record.id);
    if (error) {
      setFeedback({ type: "error", text: error.message || "The record could not be removed." });
      return;
    }
    await load();
  }

  if (loading) return <PageLoader label="Loading competency management" />;

  return (
    <div className="pguh-page-shell">
      <Header session={session} profile={profile} />
      <main className="pguh-main">
        <div className="nhsuk-width-container pguh-competency-management">
          <section className="pguh-dashboard-hero pguh-competency-hero">
            <div>
              <span className="pguh-eyebrow">Management</span>
              <h1 className="nhsuk-heading-xl">Staff competencies & certifications</h1>
              <p className="nhsuk-body-l">
                Define role requirements, record certification evidence and monitor expiry/compliance across the Trust.
              </p>
            </div>
            <div className="pguh-dashboard-identity">
              <span>Compliance attention</span>
              <strong>{summary.missing + summary.expired + summary.expiring + summary.pending}</strong>
              <small>{summary.required} role requirements tracked</small>
            </div>
          </section>

          {feedback && (
            <div className={feedback.type === "error" ? "nhsuk-error-summary" : "pguh-timesheet-message pguh-timesheet-message--success"} role="status">
              {feedback.type === "error" ? (
                <><h2 className="nhsuk-error-summary__title">Unable to complete that action</h2><div className="nhsuk-error-summary__body"><p>{feedback.text}</p></div></>
              ) : <p>{feedback.text}</p>}
            </div>
          )}

          <section className="pguh-dashboard-stats pguh-competency-summary-grid" aria-label="Competency compliance summary">
            <div className="pguh-stat-card"><span>Valid requirements</span><strong>{summary.valid}</strong><small>Verified and in date</small></div>
            <div className="pguh-stat-card"><span>Expiring ≤30 days</span><strong>{summary.expiring}</strong><small>Renewal approaching</small></div>
            <div className="pguh-stat-card"><span>Expired</span><strong>{summary.expired}</strong><small>Requires renewal</small></div>
            <div className="pguh-stat-card"><span>Missing</span><strong>{summary.missing}</strong><small>No current record</small></div>
            <div className="pguh-stat-card"><span>Awaiting verification</span><strong>{summary.pending}</strong><small>Evidence not verified</small></div>
          </section>

          <nav className="pguh-operations-tabs pguh-learning-management-tabs" aria-label="Competency management sections">
            <button type="button" className={tab === "catalogue" ? "is-active" : ""} onClick={() => setTab("catalogue")}><FaAward /> Catalogue & requirements</button>
            <button type="button" className={tab === "records" ? "is-active" : ""} onClick={() => setTab("records")}><FaClipboardCheck /> Staff records</button>
            <button type="button" className={tab === "compliance" ? "is-active" : ""} onClick={() => setTab("compliance")}><FaUsers /> Compliance</button>
          </nav>

          {tab === "catalogue" && (
            <div className="pguh-management-grid pguh-competency-management-grid">
              <section className="pguh-panel">
                <div className="pguh-panel__header">
                  <div>
                    <span className="pguh-eyebrow">Catalogue</span>
                    <h2 className="nhsuk-heading-l">{definitionDraft.id ? "Edit competency" : "Create competency"}</h2>
                  </div>
                  {definitionDraft.id && <button className="pguh-text-button" type="button" onClick={resetDefinition}>Create new</button>}
                </div>

                <form onSubmit={saveDefinition}>
                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="competency-title">Competency / certification title</label>
                    <input id="competency-title" className="nhsuk-input" value={definitionDraft.title} onChange={(event) => setDefinitionDraft((current) => ({ ...current, title: event.target.value }))} required />
                  </div>
                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="competency-category">Category</label>
                    <input id="competency-category" className="nhsuk-input" value={definitionDraft.category} onChange={(event) => setDefinitionDraft((current) => ({ ...current, category: event.target.value }))} placeholder="Clinical, Safety, Resuscitation…" />
                  </div>
                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="competency-description">Description / requirement guidance</label>
                    <textarea id="competency-description" className="nhsuk-textarea" rows="4" value={definitionDraft.description} onChange={(event) => setDefinitionDraft((current) => ({ ...current, description: event.target.value }))} />
                  </div>

                  <div className="pguh-form-grid pguh-form-grid--two">
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="competency-validity">Default validity (months)</label>
                      <span className="nhsuk-hint">Leave blank for certifications that do not expire.</span>
                      <input id="competency-validity" className="nhsuk-input nhsuk-input--width-5" type="number" min="1" value={definitionDraft.default_validity_months} onChange={(event) => setDefinitionDraft((current) => ({ ...current, default_validity_months: event.target.value }))} />
                    </div>
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="competency-renewal-lead">Renewal lead time (days)</label>
                      <input id="competency-renewal-lead" className="nhsuk-input nhsuk-input--width-5" type="number" min="1" max="365" value={definitionDraft.renewal_lead_days} onChange={(event) => setDefinitionDraft((current) => ({ ...current, renewal_lead_days: event.target.value }))} />
                    </div>
                  </div>

                  <div className="nhsuk-form-group">
                    <label className="nhsuk-label" htmlFor="competency-renewal-learning">Automatic renewal E-Learning</label>
                    <span className="nhsuk-hint">Optional. The reminder job assigns this module when the certification enters its renewal window.</span>
                    <select id="competency-renewal-learning" className="nhsuk-select" value={definitionDraft.renewal_elearning_module_id} onChange={(event) => setDefinitionDraft((current) => ({ ...current, renewal_elearning_module_id: event.target.value }))}>
                      <option value="">No automatic renewal learning</option>
                      {learningModules.map((module) => <option key={module.id} value={module.id}>{module.title}{module.mandatory ? " (Mandatory)" : ""}</option>)}
                    </select>
                  </div>

                  <fieldset className="nhsuk-fieldset nhsuk-u-margin-bottom-5">
                    <legend className="nhsuk-fieldset__legend nhsuk-fieldset__legend--s">Required job roles</legend>
                    <span className="nhsuk-hint">Staff with any selected role will see this as a required competency.</span>
                    {jobRoles.length === 0 ? (
                      <p className="nhsuk-body-s">No staff job roles are configured yet. Set them under Management → Profiles.</p>
                    ) : (
                      <div className="pguh-role-chip-grid">
                        {jobRoles.map((role) => (
                          <label className={`pguh-role-chip${definitionDraft.required_job_roles.includes(role) ? " is-selected" : ""}`} key={role}>
                            <input type="checkbox" checked={definitionDraft.required_job_roles.includes(role)} onChange={() => toggleRequiredRole(role)} />
                            <span>{role}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </fieldset>

                  <div className="nhsuk-checkboxes nhsuk-u-margin-bottom-5">
                    <div className="nhsuk-checkboxes__item">
                      <input className="nhsuk-checkboxes__input" id="competency-active" type="checkbox" checked={definitionDraft.active} onChange={(event) => setDefinitionDraft((current) => ({ ...current, active: event.target.checked }))} />
                      <label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="competency-active">Active competency</label>
                    </div>
                  </div>

                  <button className="nhsuk-button" type="submit" disabled={working}><FaSave /> {working ? "Saving…" : "Save competency"}</button>
                </form>
              </section>

              <section className="pguh-panel">
                <div className="pguh-panel__header"><div><span className="pguh-eyebrow">Current catalogue</span><h2 className="nhsuk-heading-l">Competencies</h2></div></div>
                {catalogue.length === 0 ? (
                  <div className="pguh-empty-state"><FaPlus /><h3>No competencies yet</h3><p>Create the first competency using the form.</p></div>
                ) : (
                  <div className="pguh-list-stack">
                    {catalogue.map((item) => (
                      <article className={`pguh-competency-catalogue-row${item.active ? "" : " is-inactive"}`} key={item.id}>
                        <div>
                          <div className="pguh-competency-card__topline"><span className="pguh-competency-category">{item.category}</span><span>{item.active ? "Active" : "Archived"}</span></div>
                          <h3>{item.title}</h3>
                          <p>{item.description || "No description."}</p>
                          <small>
                            {item.default_validity_months ? `${item.default_validity_months} month validity` : "No default expiry"}
                            {Array.isArray(item.required_job_roles) && item.required_job_roles.length ? ` · Required: ${item.required_job_roles.join(", ")}` : " · No role requirement"}
                          </small>
                        </div>
                        <div className="pguh-inline-actions">
                          <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => editDefinition(item)}><FaEdit /> Edit</button>
                          <button className="pguh-text-button" type="button" onClick={() => toggleDefinitionActive(item)}>{item.active ? "Archive" : "Restore"}</button>
                        </div>
                      </article>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )}

          {tab === "records" && (
            <>
              <section className="pguh-panel pguh-bulk-competency-panel">
                <div className="pguh-panel__header pguh-bulk-competency-header">
                  <div>
                    <span className="pguh-eyebrow">Bulk management</span>
                    <h2 className="nhsuk-heading-l">Mass assign competencies</h2>
                    <p className="nhsuk-body-s">Assign one or more competencies to one or more staff members in a single action. Existing records are never overwritten.</p>
                  </div>
                  <button className={bulkOpen ? "nhsuk-button nhsuk-button--secondary" : "nhsuk-button"} type="button" onClick={() => setBulkOpen((current) => !current)}>
                    <FaUsers /> {bulkOpen ? "Close bulk assignment" : "Bulk assign competencies"}
                  </button>
                </div>

                {bulkOpen && (
                  <form className="pguh-bulk-competency-form" onSubmit={saveBulkAssignment}>
                    <div className="pguh-bulk-picker-grid">
                      <fieldset className="pguh-bulk-picker">
                        <legend>1. Select staff</legend>
                        <div className="pguh-bulk-picker__toolbar">
                          <input className="nhsuk-input" value={bulkStaffSearch} onChange={(event) => setBulkStaffSearch(event.target.value)} placeholder="Search staff or role" />
                          <button className="pguh-text-button" type="button" onClick={() => setBulkVisible("staff_ids", bulkStaffOptions.map((item) => item.id), true)}>Select visible</button>
                          <button className="pguh-text-button" type="button" onClick={() => setBulkVisible("staff_ids", bulkStaffOptions.map((item) => item.id), false)}>Clear visible</button>
                        </div>
                        <div className="pguh-bulk-picker__list">
                          {bulkStaffOptions.map((staff) => (
                            <label className={`pguh-bulk-option${bulkDraft.staff_ids.includes(staff.id) ? " is-selected" : ""}`} key={staff.id}>
                              <input type="checkbox" checked={bulkDraft.staff_ids.includes(staff.id)} onChange={() => toggleBulkSelection("staff_ids", staff.id)} />
                              <span><strong>{formatProfileName(staff, staff.email || "Staff member")}</strong><small>{staff.job_role || staff.position || "Staff"}{staff.email ? ` · ${staff.email}` : ""}</small></span>
                            </label>
                          ))}
                        </div>
                        <div className="pguh-bulk-picker__count">{bulkDraft.staff_ids.length} staff selected</div>
                      </fieldset>

                      <fieldset className="pguh-bulk-picker">
                        <legend>2. Select competencies</legend>
                        <div className="pguh-bulk-picker__toolbar">
                          <input className="nhsuk-input" value={bulkCompetencySearch} onChange={(event) => setBulkCompetencySearch(event.target.value)} placeholder="Search competency or category" />
                          <button className="pguh-text-button" type="button" onClick={() => setBulkVisible("competency_ids", bulkCompetencyOptions.map((item) => item.id), true)}>Select visible</button>
                          <button className="pguh-text-button" type="button" onClick={() => setBulkVisible("competency_ids", bulkCompetencyOptions.map((item) => item.id), false)}>Clear visible</button>
                        </div>
                        <div className="pguh-bulk-picker__list">
                          {bulkCompetencyOptions.map((item) => (
                            <label className={`pguh-bulk-option${bulkDraft.competency_ids.includes(item.id) ? " is-selected" : ""}`} key={item.id}>
                              <input type="checkbox" checked={bulkDraft.competency_ids.includes(item.id)} onChange={() => toggleBulkSelection("competency_ids", item.id)} />
                              <span><strong>{item.title}</strong><small>{item.category || "General"}{item.default_validity_months ? ` · ${item.default_validity_months} month validity` : " · No expiry"}</small></span>
                            </label>
                          ))}
                        </div>
                        <div className="pguh-bulk-picker__count">{bulkDraft.competency_ids.length} competencies selected</div>
                      </fieldset>
                    </div>

                    <div className="pguh-bulk-assignment-options">
                      <div className="pguh-form-grid pguh-form-grid--two">
                        <div className="nhsuk-form-group">
                          <label className="nhsuk-label" htmlFor="bulk-competency-achieved">Common achieved / issued date</label>
                          <span className="nhsuk-hint">Optional. Each competency's own default validity is used to calculate its expiry date.</span>
                          <input id="bulk-competency-achieved" className="nhsuk-input" type="date" value={bulkDraft.achieved_on} onChange={(event) => setBulkDraft((current) => ({ ...current, achieved_on: event.target.value }))} />
                        </div>
                        <div className="nhsuk-form-group">
                          <label className="nhsuk-label">Assignments to create</label>
                          <div className="pguh-bulk-combination-count">
                            <strong>{bulkDraft.staff_ids.length * bulkDraft.competency_ids.length}</strong> selected combinations
                            <small>Existing staff/competency records will be skipped automatically.</small>
                          </div>
                        </div>
                      </div>

                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label" htmlFor="bulk-competency-evidence-notes">Shared evidence / verification notes</label>
                        <textarea id="bulk-competency-evidence-notes" className="nhsuk-textarea" rows="2" value={bulkDraft.evidence_notes} onChange={(event) => setBulkDraft((current) => ({ ...current, evidence_notes: event.target.value }))} />
                      </div>
                      <div className="nhsuk-form-group">
                        <label className="nhsuk-label" htmlFor="bulk-competency-notes">Shared internal notes</label>
                        <textarea id="bulk-competency-notes" className="nhsuk-textarea" rows="2" value={bulkDraft.notes} onChange={(event) => setBulkDraft((current) => ({ ...current, notes: event.target.value }))} />
                      </div>

                      <div className="nhsuk-checkboxes nhsuk-u-margin-bottom-5">
                        <div className="nhsuk-checkboxes__item">
                          <input className="nhsuk-checkboxes__input" id="bulk-competency-verified" type="checkbox" checked={bulkDraft.verified} onChange={(event) => setBulkDraft((current) => ({ ...current, verified: event.target.checked }))} />
                          <label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="bulk-competency-verified">Mark all newly-created records as Management verified</label>
                        </div>
                      </div>

                      <div className="pguh-inline-actions pguh-bulk-assignment-actions">
                        <button className="nhsuk-button" type="submit" disabled={working || !bulkDraft.staff_ids.length || !bulkDraft.competency_ids.length}><FaPlus /> {working ? "Assigning…" : "Create assignments"}</button>
                        <button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={resetBulkAssignment} disabled={working}>Reset selection</button>
                      </div>
                    </div>
                  </form>
                )}
              </section>

              <section className="pguh-panel">
                <div className="pguh-panel__header">
                  <div><span className="pguh-eyebrow">Certification record</span><h2 className="nhsuk-heading-l">{recordDraft.id ? "Edit staff record" : "Record a competency"}</h2></div>
                  {recordDraft.id && <button className="pguh-text-button" type="button" onClick={resetRecord}>New record</button>}
                </div>
                <form onSubmit={saveRecord}>
                  <div className="pguh-form-grid pguh-form-grid--two">
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="competency-staff">Staff member</label>
                      <select id="competency-staff" className="nhsuk-select" value={recordDraft.user_id} onChange={(event) => updateRecordField("user_id", event.target.value)} required>
                        <option value="">Select staff member</option>
                        {profiles.map((staff) => <option key={staff.id} value={staff.id}>{formatProfileName(staff, staff.email || "Staff member")} — {staff.job_role || staff.position}</option>)}
                      </select>
                    </div>
                    <div className="nhsuk-form-group">
                      <label className="nhsuk-label" htmlFor="competency-definition">Competency / certification</label>
                      <select id="competency-definition" className="nhsuk-select" value={recordDraft.competency_id} onChange={(event) => updateRecordField("competency_id", event.target.value)} required>
                        <option value="">Select competency</option>
                        {catalogue.filter((item) => item.active || item.id === recordDraft.competency_id).map((item) => <option key={item.id} value={item.id}>{item.title}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="pguh-form-grid pguh-form-grid--two">
                    <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="competency-achieved">Achieved / issued date</label><input id="competency-achieved" className="nhsuk-input" type="date" value={recordDraft.achieved_on} onChange={(event) => updateRecordField("achieved_on", event.target.value)} /></div>
                    <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="competency-expiry">Expiry date</label><span className="nhsuk-hint">Auto-filled from the catalogue validity where configured; you can override it.</span><input id="competency-expiry" className="nhsuk-input" type="date" value={recordDraft.expires_on} onChange={(event) => updateRecordField("expires_on", event.target.value)} /></div>
                  </div>

                  <div className="pguh-form-grid pguh-form-grid--two">
                    <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="competency-reference">Certificate / registration reference</label><input id="competency-reference" className="nhsuk-input" value={recordDraft.certificate_reference} onChange={(event) => updateRecordField("certificate_reference", event.target.value)} /></div>
                    <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="competency-evidence-url">Evidence link</label><input id="competency-evidence-url" className="nhsuk-input" type="url" value={recordDraft.evidence_url} onChange={(event) => updateRecordField("evidence_url", event.target.value)} placeholder="https://…" /></div>
                  </div>

                  <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="competency-evidence-notes">Evidence / verification notes</label><textarea id="competency-evidence-notes" className="nhsuk-textarea" rows="3" value={recordDraft.evidence_notes} onChange={(event) => updateRecordField("evidence_notes", event.target.value)} /></div>
                  <div className="nhsuk-form-group"><label className="nhsuk-label" htmlFor="competency-record-notes">Internal record notes</label><textarea id="competency-record-notes" className="nhsuk-textarea" rows="3" value={recordDraft.notes} onChange={(event) => updateRecordField("notes", event.target.value)} /></div>

                  <div className="nhsuk-checkboxes nhsuk-u-margin-bottom-5">
                    <div className="nhsuk-checkboxes__item"><input className="nhsuk-checkboxes__input" id="competency-verified" type="checkbox" checked={recordDraft.verified} onChange={(event) => updateRecordField("verified", event.target.checked)} /><label className="nhsuk-label nhsuk-checkboxes__label" htmlFor="competency-verified">Evidence verified by Management</label></div>
                  </div>
                  <button className="nhsuk-button" type="submit" disabled={working}><FaSave /> {working ? "Saving…" : "Save staff record"}</button>
                </form>
              </section>

              <section className="pguh-panel">
                <div className="pguh-panel__header"><div><span className="pguh-eyebrow">Recorded evidence</span><h2 className="nhsuk-heading-l">Staff certification records</h2></div><input className="nhsuk-input pguh-compact-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search staff, role or competency" /></div>
                <div className="pguh-table-scroll">
                  <table className="nhsuk-table nhsuk-table--striped pguh-competency-table">
                    <thead><tr><th>Staff</th><th>Role</th><th>Competency</th><th>Achieved</th><th>Expires</th><th>Status</th><th>Verification</th><th>Reference</th><th>Actions</th></tr></thead>
                    <tbody>
                      {filteredRecords.length === 0 ? <tr><td colSpan="9">No competency records match the current search.</td></tr> : filteredRecords.map((record) => {
                        const staff = profileMap[record.user_id];
                        const definition = definitionMap[record.competency_id];
                        const requiredForRole = isCompetencyRequiredForRole(definition, staff?.job_role);
                        const status = getCompetencyStatus(record, { required: requiredForRole });
                        return (
                          <tr key={record.id}>
                            <td><strong>{formatProfileName(staff, staff?.email || "Unknown staff")}</strong></td>
                            <td>{staff?.job_role || staff?.position || "—"}</td>
                            <td>{definition?.title || "Unknown competency"}</td>
                            <td>{formatCompetencyDate(record.achieved_on)}</td>
                            <td>{formatCompetencyDate(record.expires_on, "No expiry")}</td>
                            <td><span className={competencyStatusClass(status)}>{status.label}</span></td>
                            <td>
                              <strong className="pguh-verification-state">{verificationLabel(record)}</strong>
                              {record.verified_at && <small className="pguh-table-subtext">{new Date(record.verified_at).toLocaleString("en-GB")}</small>}
                            </td>
                            <td>{record.certificate_reference || "—"}</td>
                            <td>
                              <div className="pguh-inline-actions pguh-inline-actions--stackable">
                                {(!record.verified || record.verification_method === "self") && isEnabled("feature.competency_management_verification") && (
                                  <button
                                    className="nhsuk-button pguh-competency-verify-button pguh-competency-verify-button--management"
                                    type="button"
                                    disabled={verifyingRecordId === record.id}
                                    onClick={() => managementVerify(record)}
                                  >
                                    <FaCheckCircle /> {verifyingRecordId === record.id ? "Verifying…" : record.verification_method === "self" ? "Management verify" : "Verify"}
                                  </button>
                                )}
                                <button className="pguh-text-button" type="button" onClick={() => editRecord(record)}>Edit</button>
                                <button className="pguh-text-button pguh-text-button--danger" type="button" onClick={() => deleteRecord(record)}>Remove</button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </section>
            </>
          )}

          {tab === "compliance" && (
            <section className="pguh-panel">
              <div className="pguh-panel__header">
                <div><span className="pguh-eyebrow">Trust compliance</span><h2 className="nhsuk-heading-l">Role requirement matrix</h2></div>
              </div>
              <div className="pguh-filter-bar">
                <input className="nhsuk-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search staff, job role or competency" />
                <select className="nhsuk-select" value={complianceFilter} onChange={(event) => setComplianceFilter(event.target.value)}>
                  <option value="attention">Attention required</option>
                  <option value="all">All requirements</option>
                  <option value="valid">Valid</option>
                  <option value="expiring">Expiring soon</option>
                  <option value="expired">Expired</option>
                  <option value="missing">Missing</option>
                  <option value="pending">Awaiting verification</option>
                </select>
              </div>
              <div className="pguh-table-scroll">
                <table className="nhsuk-table nhsuk-table--striped pguh-competency-table">
                  <thead><tr><th>Staff member</th><th>Job role</th><th>Required competency</th><th>Expires</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>
                    {filteredComplianceRows.length === 0 ? <tr><td colSpan="6">No role requirements match this filter.</td></tr> : filteredComplianceRows.map((row) => (
                      <tr key={`${row.staff.id}:${row.definition.id}`}>
                        <td><strong>{formatProfileName(row.staff, row.staff.email || "Staff member")}</strong><small className="pguh-table-subtext">{row.staff.email}</small></td>
                        <td>{row.staff.job_role || "Not assigned"}</td>
                        <td>{row.definition.title}</td>
                        <td>{formatCompetencyDate(row.record?.expires_on, row.record ? "No expiry" : "—")}</td>
                        <td><span className={competencyStatusClass(row.status)}>{row.status.label}</span></td>
                        <td>
                          <div className="pguh-inline-actions pguh-inline-actions--stackable">
                            {row.record && (!row.record.verified || row.record.verification_method === "self") && isEnabled("feature.competency_management_verification") && (
                              <button className="nhsuk-button pguh-competency-verify-button pguh-competency-verify-button--management" type="button" disabled={verifyingRecordId === row.record.id} onClick={() => managementVerify(row.record)}>
                                <FaCheckCircle /> {verifyingRecordId === row.record.id ? "Verifying…" : "Verify"}
                              </button>
                            )}
                            <button className="pguh-text-button" type="button" onClick={() => { if (row.record) editRecord(row.record); else { setRecordDraft({ ...EMPTY_RECORD, user_id: row.staff.id, competency_id: row.definition.id }); setTab("records"); window.scrollTo({ top: 0, behavior: "smooth" }); } }}>{row.record ? "Edit record" : "Record evidence"}</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {(summary.expired > 0 || summary.missing > 0) && (
                <div className="nhsuk-warning-callout nhsuk-u-margin-top-5">
                  <h3 className="nhsuk-warning-callout__label"><span role="text"><FaExclamationTriangle /> Compliance attention</span></h3>
                  <p>{summary.expired} expired and {summary.missing} missing role requirement{summary.missing === 1 ? "" : "s"} currently require Management action.</p>
                </div>
              )}
            </section>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
